import { Controller } from "@hotwired/stimulus"

// Keeps unsaved text in localStorage so a reload, a crashed WebView, or an
// accidental dismissal doesn't throw away what the user typed. A draft is only
// restored while the field still holds the value it was drafted against, so an
// edit saved from another device is never overwritten by a stale draft.
const STORAGE_PREFIX = "form-draft:"

export default class extends Controller {
  static targets = ["field"]

  static values = {
    key: String,
    maxAgeDays: { type: Number, default: 7 }
  }

  connect() {
    this.element.addEventListener("turbo:submit-end", this.submitEnded)

    // Let Alpine bind x-model and the navigation guard take its baseline first,
    // so the restored text wins and the guard sees it as an unsaved edit.
    this.restoreTimeout = setTimeout(() => this.restore(), 0)
    this.pruneExpired()
  }

  disconnect() {
    clearTimeout(this.restoreTimeout)
    this.element.removeEventListener("turbo:submit-end", this.submitEnded)
  }

  save(event) {
    const field = event.target
    const drafts = this.drafts
    const base = this.baseValue(field)

    if (field.value === base) {
      delete drafts[field.name]
    } else {
      drafts[field.name] = { value: field.value, base }
    }

    this.write(drafts)
  }

  restore() {
    const drafts = this.drafts

    this.fieldTargets.forEach((field) => {
      const draft = drafts[field.name]
      const base = this.baseValue(field)
      if (!draft || draft.base !== base || field.value !== base || draft.value === field.value) return

      field.value = draft.value
      field.dispatchEvent(new Event("input", { bubbles: true }))
    })
  }

  baseValue(field) {
    // A validation response renders unsaved input as defaultValue. Keep the
    // persisted text as the baseline, normalizing newlines like a textarea.
    return (field.dataset.formDraftBase ?? field.defaultValue).replace(/\r\n?/g, "\n")
  }

  submitEnded = (event) => {
    if (event.detail.success) this.clear()
  }

  clear() {
    try {
      localStorage.removeItem(this.storageKey)
    } catch {}
  }

  write(drafts) {
    try {
      if (Object.keys(drafts).length === 0) {
        localStorage.removeItem(this.storageKey)
      } else {
        localStorage.setItem(this.storageKey, JSON.stringify({ fields: drafts, savedAt: Date.now() }))
      }
    } catch {}
  }

  pruneExpired() {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(STORAGE_PREFIX))
        .forEach((key) => {
          if (this.expired(this.parse(localStorage.getItem(key)))) localStorage.removeItem(key)
        })
    } catch {}
  }

  expired(entry) {
    return !entry || Date.now() - entry.savedAt > this.maxAgeDaysValue * 24 * 60 * 60 * 1000
  }

  parse(raw) {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  get drafts() {
    try {
      const entry = this.parse(localStorage.getItem(this.storageKey))
      return this.expired(entry) ? {} : entry.fields
    } catch {
      return {}
    }
  }

  get storageKey() {
    return `${STORAGE_PREFIX}${this.keyValue}`
  }
}
